import { Box, Typography, IconButton, CircularProgress, Divider, useTheme, lighten } from '@mui/material';
import { Close, Group, PersonAddAlt1, PersonOff, SubdirectoryArrowRight } from '@mui/icons-material';
import { useState, useCallback, useEffect } from 'react';
import { getAllSelections } from 'src/shared/services/groupMessageService';
import { getMembers } from 'src/shared/services/memberService';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { Member } from 'src/modules/member/models/member';
import { useTranslation } from 'react-i18next';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { useFormContext } from 'react-hook-form';
import { InferType } from 'yup';
import { makeStyles } from '@mui/styles';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { EmailSubscriptionHandler } from 'src/modules/member/components/emailSubscriptionHandler';
import { SmsSubscriptionHandler } from 'src/modules/member/components/smsSubscriptionHandler';
import SelectionAutocomplete from './selectionAutocomplete';
import { Selection } from '../models/selection';
import { useSchemaCreateGroupMessageEmail } from '../schemas/useSchemaCreateGroupMessageEmail';
import { GroupMessageControlledSwitch } from './groupMessageControlledSwitch';

type SelectionListProps = {
  smsMode: boolean;
  callback: (item: Selection | null) => void;
  activeSelection: Selection | null;
  selectedRows: Set<number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
};

export const SelectionList = (props: SelectionListProps) => {
  const { smsMode, callback, selectedRows, setSelectedRows, activeSelection } = props;
  const { t } = useTranslation(['member']);
  const theme = useTheme();

  const useStyles = makeStyles({
    selected: {
      textDecoration: 'line-through',
      backgroundColor: `${lighten(theme.palette.error.main, 0.9)} !important`,
      borderLeft: `1px solid ${theme.palette.error.dark}`,
    },
  });
  const classes = useStyles();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectionArray, setSelectionArray] = useState<Selection[]>([]);
  const [excludeNoEmails, setExcludeNoEmails] = useState<boolean>(true);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'email', sort: 'asc' }]);

  const [rows, setRows] = useState<Member[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: 10,
    page: 0,
  });

  const createGroupMessageEmailSchema = useSchemaCreateGroupMessageEmail();
  type CreateGroupMessageFormInterface = InferType<typeof createGroupMessageEmailSchema>;

  const { setValue, getValues } = useFormContext<CreateGroupMessageFormInterface>();

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const excludeMemberHandler = (member: Member) => {
    const prev = getValues('excludedMemberFromSelection') || [];
    if (prev) {
      const isMemberExcluded = prev.some((m) => m.member_id === member.member_id);
      if (isMemberExcluded) {
        // Remove the member if already excluded
        setValue(
          'excludedMemberFromSelection',
          prev.filter((m) => m.member_id !== member.member_id),
        );
        setSelectedRows((prevRow) => {
          const newSet = new Set(prevRow);
          newSet.delete(member.member_id ?? 0);
          return newSet;
        });
      } else {
        // Add the member if not excluded
        setValue('excludedMemberFromSelection', [
          ...prev,
          { member_id: member.member_id, firstName: member.firstName, lastName: member.lastName, email: member.email },
        ]);
        setSelectedRows((prevRow) => new Set(prevRow).add(member.member_id ?? 0));
      }
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllSelections(0, 500, 'selection_id', 'desc');
      if (isMounted()) {
        setSelectionArray(response.data.results);
        setLoading(false);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sätter rows, baserat på excludeMembersWithoutEmail-filter
  useEffect(() => {
    const fetchMemberData = async () => {
      setLoading(true);
      updateData('loading', true);
      try {
        const result = await getMembers(
          data.page,
          data.pageSize,
          sortModel[0].field,
          sortModel[0].sort,
          {
            selectionId: activeSelection?.selection_id,

            excludeMembersWithoutEmail: !smsMode && excludeNoEmails ? 'true' : 'false',
            status: [MemberStatusEnum.Paid, MemberStatusEnum.NotPaid],
          } as MemberSearchFilter,
          [],
          '',
        );
        if (isMounted()) {
          const memberResult = extendObjectWithId(result.data.results, 'member_id');
          updateData('totalRows', result.data.totalItems);
          setRows(memberResult);
        }
      } catch (error) {
        if (isMounted()) {
          ConsoleHelper.log(error);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
          updateData('loading', false);
        }
      }
    };

    if (activeSelection?.selection_id) {
      fetchMemberData();
    }
  }, [data.page, data.pageSize, activeSelection, isMounted, sortModel, excludeNoEmails, smsMode]);

  const onChange = useCallback(
    (item: Selection | null) => {
      callback(item);
    },
    [callback],
  );

  const columns: GridColDef[] = smsMode
    ? [
        { field: 'memberNr', headerName: t('member:tableHeaders.member_id'), flex: 0.5 },
        {
          field: 'lastName',
          headerName: t('member:tableHeaders.name'),
          sortable: false,
          flex: 1,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return (
              <>
                {params.row.firstName || ''} {params.row.lastName || ''}
              </>
            );
          },
        },
        {
          field: 'phone',
          headerName: t('member:tableHeaders.phone'),
          sortable: false,
          flex: 1,
          renderCell: (params: GridCellParams) => {
            const { phone, phoneOther, newsletterSMSUnsubscribed } = params.row;
            return (
              <Box display='flex' alignItems='center'>
                {`${[phone, phoneOther].filter((x) => x !== '').join(', ')}`}
                <SmsSubscriptionHandler unsubscribed={newsletterSMSUnsubscribed} />
              </Box>
            );
          },
        },
        {
          field: 'email',
          headerName: t('member:tableHeaders.email'),
          sortable: false,
          flex: 1,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return <>{params.row.email || ''}</>;
          },
        },
      ]
    : [
        {
          field: 'lastName',
          headerName: t('member:tableHeaders.name'),
          sortable: false,
          flex: 2,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return (
              <>
                {params.row.firstName || ''} {params.row.lastName || ''}
              </>
            );
          },
        },
        {
          field: 'email',
          headerName: t('member:tableHeaders.email'),
          sortable: false,
          flex: 2,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return (
              <Box display='flex' alignItems='center'>
                {params.row.email || ''}
                <EmailSubscriptionHandler unsubscribed={params.row.newsletterEmailUnsubscribed} />
              </Box>
            );
          },
        },
        {
          field: 'action',
          headerName: t('groupMessage:tableHeaders.exclude'),
          headerAlign: 'right',
          align: 'right',
          sortable: false,
          flex: 1,
          minWidth: 150,
          type: 'actions',

          renderCell: (params: GridCellParams) => {
            return (
              <>
                <IconButton type='button' onClick={() => excludeMemberHandler(params.row)}>
                  {selectedRows.has(params.row.member_id) ? <PersonAddAlt1 color='primary' /> : <PersonOff color='primary' />}
                </IconButton>
              </>
            );
          },
        },
      ];

  return (
    <>
      {loading && <CircularProgress />}
      {!loading && selectionArray && !activeSelection && (
        <SelectionAutocomplete<Selection>
          items={selectionArray}
          callback={(item: Selection | null) => onChange(item)}
          label='formFields.selection'
          nameProp='selectionName'
          valueProp='selection_id'
        />
      )}
      {!loading && selectionArray && activeSelection && (
        <>
          <Typography
            variant='h6'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              borderColor: 'divider',
              borderWidth: 1,
              borderStyle: 'solid',
              borderRadius: 2,
              px: 2,
              py: 'none',
              mb: 1,
            }}
          >
            <Group sx={{ mr: 1 }} />
            {activeSelection.selectionName}
            {!loading && ` (${activeSelection.memberCount})`}
            <IconButton sx={{ ml: 1 }} onClick={() => onChange(null)}>
              <Close />
            </IconButton>
          </Typography>
          {smsMode ? (
            <Typography
              variant='body1'
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 2,
                mb: 3,
              }}
            >
              <SubdirectoryArrowRight sx={{ mr: 1 }} />
              {t('groupMessage:sms.recipiantsWithPhone', {
                modifiedCount: activeSelection.memberCountWithMobilePhone,
                count: activeSelection.memberCount,
              })}
            </Typography>
          ) : (
            <Typography
              variant='body1'
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 2,
                mb: 3,
              }}
            >
              <SubdirectoryArrowRight sx={{ mr: 1 }} />
              {t('groupMessage:email.recipiantsWithEmail', {
                modifiedCount: activeSelection.memberCountWithEmail,
                count: activeSelection.memberCount,
              })}
            </Typography>
          )}
          <Box sx={{ height: 350, overflowY: 'auto', p: 1, m: -1 }}>
            <Divider />
            <DataGrid<Member>
              filterInToolbar={
                !smsMode ? (
                  <GroupMessageControlledSwitch
                    label={t('groupMessage:email.hideMemberNoEmail')}
                    toggleState={excludeNoEmails}
                    setToggleState={setExcludeNoEmails}
                  />
                ) : undefined
              }
              autoHeight
              columns={columns}
              rows={rows}
              dataModel={data}
              disableSelectionOnClick
              getRowClassName={(params) => (selectedRows.has(params.row.member_id ?? 0) ? classes.selected : '')}
              onSelectionModelChange={(newSelectionModel) => {
                setSelectionModel(newSelectionModel);
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                if (newSortModel.length) {
                  setSortModel(newSortModel);
                } else {
                  setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
                }
              }}
              checkboxSelection={false}
              selectionModel={selectionModel}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </Box>
        </>
      )}
    </>
  );
};
